import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import PageContainer from "@/components/layouts/PageContainer";
import { Heading } from "@/components/heading";
import { Text } from "@/components/text";
import { Button } from "@/components/button";
import { ArrowLeftIcon } from "@heroicons/react/20/solid";
import { useUpdateExamAssignment } from "@/apis/exam_assignments";
import { useQueryGetExamAssignment } from "@/apis/exam_assignments/getExamAssignment";
import ExamAssignmentForm from "./ExamAssignmentForm";



const ExamAssignmentUpdatePage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [serverError, setServerError] = useState<string | null>(null);

  // API 호출
  const { mutate: updateExamAssignment, isPending } = useUpdateExamAssignment();

  // 기존 할당 정보 조회
  const { data: assignmentData, isLoading: isLoadingAssignment } =
    useQueryGetExamAssignment(id || "");

  // 폼 제출 핸들러
  const handleSubmit = (formData: { title: string }) => {
    if (!id) return;

    updateExamAssignment(
      {
        id,
        input: {
          title: formData.title,
        },
      },
      {
        onSuccess: () => {
          navigate(`/exam_assignments/${id}`);
        },
        onError: (error: any) => {
          setServerError(error?.message || "Failed to update exam assignment");
        },
      }
    );
  };

  // 취소 핸들러
  const handleCancel = () => {
    navigate(`/exam_assignments/${id}`);
  };

  if (isLoadingAssignment) {
    return (
      <PageContainer>
        <div className="text-center py-8">Loading assignment details...</div>
      </PageContainer>
    );
  }

  if (!assignmentData) {
    return (
      <PageContainer>
        <div className="text-center py-8 text-red-600">
          Assignment not found
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button plain onClick={handleCancel}>
            <ArrowLeftIcon className="h-5 w-5" />
            Back
          </Button>

          <div>
            <Heading>Edit Exam Assignment</Heading>
            <Text className="text-zinc-500">
              Update the title of this exam assignment.
            </Text>
          </div>
        </div>

        {/* 서버 오류 표시 */}
        {serverError && (
          <div className="p-4 bg-red-50 text-red-600 rounded-md">
            {serverError}
          </div>
        )}

        {/* 수정 폼 */}
        <ExamAssignmentForm
          initialValues={{
            title: assignmentData.title,
            // Legacy fields for compatibility
            exam_set_id: assignmentData.exam_set_id,
          }}
          isPending={isPending}
          onSubmit={handleSubmit}
          submitLabel="Update Assignment"
          readOnly={false}
          isEdit={true}
        />
      </div>
    </PageContainer>
  );
};

export default ExamAssignmentUpdatePage;
